'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Eye, EyeOff, ChevronDown, ChevronRight } from 'lucide-react';
import {
  VISIBILITY_REGISTRY,
  REGISTRY_SIZE,
  VISIBILITY_PRESETS,
  PRESET_META,
  DEFAULT_PREFERENCES,
  IMPORTANCE_LABELS,
  TECH_LEVEL_LABELS,
  INVESTIGATIVE_LABELS,
} from '@/lib/visibility';
import type { VisibilityPresetName, VisibilityPreferences } from '@/lib/visibility';

// ── Storage (same key as the context provider) ──────────────────────────────

const STORAGE_KEY = 'pref:call_layout_visibility_v2';

function readPrefs(): VisibilityPreferences {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw);
    return {
      activePreset: parsed.activePreset ?? DEFAULT_PREFERENCES.activePreset,
      importanceThreshold: parsed.importanceThreshold ?? DEFAULT_PREFERENCES.importanceThreshold,
      techLevelThreshold: parsed.techLevelThreshold ?? DEFAULT_PREFERENCES.techLevelThreshold,
      investigativeThreshold: parsed.investigativeThreshold ?? DEFAULT_PREFERENCES.investigativeThreshold,
      overrides: parsed.overrides ?? {},
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function writePrefs(prefs: VisibilityPreferences) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    window.dispatchEvent(new StorageEvent('storage', {
      key: STORAGE_KEY,
      newValue: JSON.stringify(prefs),
    }));
  } catch { /* ignore */ }
}

function computeHiddenCount(prefs: VisibilityPreferences): number {
  let hidden = 0;
  for (const [id, entry] of Object.entries(VISIBILITY_REGISTRY)) {
    if (entry.critical) continue;
    const override = prefs.overrides[id];
    if (override === true) continue;
    if (override === false) { hidden++; continue; }
    const ok =
      entry.importance >= prefs.importanceThreshold &&
      entry.techLevel <= prefs.techLevelThreshold &&
      entry.investigativeValue >= prefs.investigativeThreshold;
    if (!ok) hidden++;
  }
  return hidden;
}

// ── Compact slider ──────────────────────────────────────────────────────────

function MiniSlider({
  label,
  value,
  max,
  labels,
  color,
  onChange,
}: {
  label: string;
  value: number;
  max: number;
  labels: Record<number, string>;
  color: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-white/70 uppercase tracking-wider">{label}</span>
        <span className={`text-[10px] font-semibold ${color}`}>{labels[value]}</span>
      </div>
      <input
        type="range"
        min={0}
        max={max}
        step={1}
        value={value}
        onChange={(e) => { e.stopPropagation(); onChange(Number(e.target.value)); }}
        onClick={(e) => e.stopPropagation()}
        className="w-full h-1 rounded-full appearance-none cursor-pointer bg-slate-700
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border
          [&::-webkit-slider-thumb]:border-blue-500 [&::-webkit-slider-thumb]:shadow [&::-webkit-slider-thumb]:cursor-pointer
          [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-blue-500
          [&::-moz-range-thumb]:cursor-pointer"
      />
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export function InlineVisibilityControls() {
  const [prefs, setPrefs] = useState<VisibilityPreferences>(readPrefs);
  const [expanded, setExpanded] = useState(false);

  // Track what we last wrote so the storage listener can skip self-triggered events.
  // Value comparison is more robust than timing-based flags (no microtask race window).
  const lastWrittenRef = useRef<string>('');

  useEffect(() => {
    const serialized = JSON.stringify(prefs);
    lastWrittenRef.current = serialized;
    writePrefs(prefs);
  }, [prefs]);

  // Listen for changes from settings page or other tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      // Skip if this is our own write (same serialized value)
      if (e.newValue === lastWrittenRef.current) return;
      try { setPrefs(JSON.parse(e.newValue)); } catch { /* ignore */ }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const hiddenCount = useMemo(() => computeHiddenCount(prefs), [prefs]);
  const visibleCount = REGISTRY_SIZE - hiddenCount;

  const applyPreset = useCallback((preset: VisibilityPresetName) => {
    const t = VISIBILITY_PRESETS[preset];
    if (!t) return;
    setPrefs({ activePreset: preset, ...t, overrides: {} });
  }, []);

  const updateThreshold = useCallback((key: keyof VisibilityPreferences, value: number) => {
    setPrefs(prev => ({ ...prev, [key]: value, activePreset: 'custom' as VisibilityPresetName }));
  }, []);

  const presetOrder: VisibilityPresetName[] = ['essentials', 'standard', 'advanced', 'full', 'training', 'developer'];

  return (
    <div className="border-t border-white/[0.06] py-1.5 px-1.5" onClick={(e) => e.stopPropagation()}>
      {/* Collapsed row — click to expand */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } }}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors group cursor-pointer select-none"
      >
        <div className="p-1.5 rounded bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
          {hiddenCount > 0 ? (
            <EyeOff className="w-4 h-4 text-emerald-400 transition-colors" />
          ) : (
            <Eye className="w-4 h-4 text-emerald-400 transition-colors" />
          )}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="font-medium text-white/80 group-hover:text-white transition-colors">
            Layout: {PRESET_META[prefs.activePreset]?.label ?? 'Custom'}
          </p>
          <p className="text-[10px] text-white/70 mt-0.5">
            {visibleCount}/{REGISTRY_SIZE} visible
            {hiddenCount > 0 && <span className="text-red-400/60"> · {hiddenCount} hidden</span>}
          </p>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-white/60" />
        ) : (
          <ChevronRight className="w-4 h-4 text-white/60" />
        )}
      </div>

      {/* Expanded controls */}
      {expanded && (
        <div className="mt-1.5 mx-1 space-y-3 px-2 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.06]">
          {/* Preset chips */}
          <div className="flex flex-wrap gap-1">
            {presetOrder.map((p) => (
              <button
                key={p}
                onClick={(e) => { e.stopPropagation(); applyPreset(p); }}
                className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                  prefs.activePreset === p
                    ? 'bg-blue-600/30 text-blue-300 border border-blue-500/40'
                    : 'bg-white/[0.03] text-white/70 border border-white/[0.06] hover:bg-white/[0.06] hover:text-white/70'
                }`}
              >
                {PRESET_META[p].label}
              </button>
            ))}
          </div>

        </div>
      )}
    </div>
  );
}
